/**
 * Intelligent mapping rules from General categories to Federal and State tax categories
 * These rules help automatically suggest tax categories based on the general category selected
 */

interface CategoryMappingRule {
  generalCategory: string;
  federalCategory: string | null;
  oregonCategory: string | null;
  confidence: number;
  context?: string;
}

/**
 * Mapping rules from General categories to Federal/State tax categories
 * Based on common tax filing patterns and business expense categorization
 */
export const GENERAL_TO_TAX_MAPPINGS: Record<string, CategoryMappingRule> = {
  // ========================================
  // HOUSING & UTILITIES
  // ========================================
  Rent: {
    generalCategory: "Rent",
    federalCategory: "rent_lease",
    oregonCategory: "rent_lease",
    confidence: 0.95,
  },
  Mortgage: {
    generalCategory: "Mortgage",
    federalCategory: "interest",
    oregonCategory: "interest",
    confidence: 0.9,
    context: "Mortgage interest is deductible",
  },
  Electric: {
    generalCategory: "Electric",
    federalCategory: "utilities",
    oregonCategory: "utilities",
    confidence: 0.9,
  },
  Water: {
    generalCategory: "Water",
    federalCategory: "utilities",
    oregonCategory: "utilities",
    confidence: 0.9,
  },
  Gas: {
    generalCategory: "Gas",
    federalCategory: "utilities",
    oregonCategory: "utilities",
    confidence: 0.9,
  },
  Trash: {
    generalCategory: "Trash",
    federalCategory: "utilities",
    oregonCategory: "utilities",
    confidence: 0.9,
  },
  "Internet Service": {
    generalCategory: "Internet Service",
    federalCategory: "utilities",
    oregonCategory: "utilities",
    confidence: 0.85,
  },
  "Cable TV": {
    generalCategory: "Cable TV",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.8,
    context: "Usually personal expense",
  },
  "Mobile Phone": {
    generalCategory: "Mobile Phone",
    federalCategory: "utilities",
    oregonCategory: "utilities",
    confidence: 0.8,
    context: "If used for business",
  },
  Utilities: {
    generalCategory: "Utilities",
    federalCategory: "utilities",
    oregonCategory: "utilities",
    confidence: 0.95,
  },

  // ========================================
  // TRANSPORTATION
  // ========================================
  "Gas Stations": {
    generalCategory: "Gas Stations",
    federalCategory: "car_truck",
    oregonCategory: "car_truck_expenses",
    confidence: 0.8,
    context: "Business vehicle use",
  },
  Parking: {
    generalCategory: "Parking",
    federalCategory: "car_truck",
    oregonCategory: "car_truck_expenses",
    confidence: 0.75,
  },
  "Auto Maintenance": {
    generalCategory: "Auto Maintenance",
    federalCategory: "car_truck",
    oregonCategory: "car_truck_expenses",
    confidence: 0.85,
  },
  "Auto Insurance": {
    generalCategory: "Auto Insurance",
    federalCategory: "car_truck",
    oregonCategory: "insurance",
    confidence: 0.85,
  },
  "Public Transportation": {
    generalCategory: "Public Transportation",
    federalCategory: "travel",
    oregonCategory: "travel",
    confidence: 0.7,
  },
  Rideshare: {
    generalCategory: "Rideshare",
    federalCategory: "travel",
    oregonCategory: "travel",
    confidence: 0.7,
    context: "If for business travel",
  },
  Taxi: {
    generalCategory: "Taxi",
    federalCategory: "travel",
    oregonCategory: "travel",
    confidence: 0.7,
  },

  // ========================================
  // FOOD & DINING
  // ========================================
  Groceries: {
    generalCategory: "Groceries",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.95,
    context: "Usually personal expense",
  },
  Restaurants: {
    generalCategory: "Restaurants",
    federalCategory: "meals",
    oregonCategory: "meals",
    confidence: 0.5,
    context: "If business meal",
  },
  "Fast Food": {
    generalCategory: "Fast Food",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.9,
    context: "Usually personal expense",
  },
  "Coffee Shops": {
    generalCategory: "Coffee Shops",
    federalCategory: "meals",
    oregonCategory: "meals",
    confidence: 0.4,
    context: "If business meeting",
  },
  "Bars and Nightlife": {
    generalCategory: "Bars and Nightlife",
    federalCategory: "meals",
    oregonCategory: "meals",
    confidence: 0.3,
    context: "If client entertainment",
  },
  "Food and Drink": {
    generalCategory: "Food and Drink",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.7,
  },

  // ========================================
  // PROFESSIONAL SERVICES
  // ========================================
  Legal: {
    generalCategory: "Legal",
    federalCategory: "legal_professional",
    oregonCategory: "legal_professional_services",
    confidence: 0.95,
  },
  Accounting: {
    generalCategory: "Accounting",
    federalCategory: "legal_professional",
    oregonCategory: "legal_professional_services",
    confidence: 0.95,
  },
  Consulting: {
    generalCategory: "Consulting",
    federalCategory: "legal_professional",
    oregonCategory: "legal_professional_services",
    confidence: 0.95,
  },
  "Professional Services": {
    generalCategory: "Professional Services",
    federalCategory: "legal_professional",
    oregonCategory: "legal_professional_services",
    confidence: 0.9,
  },
  "Business Services": {
    generalCategory: "Business Services",
    federalCategory: "other_expenses",
    oregonCategory: "other_expenses",
    confidence: 0.8,
  },

  // ========================================
  // OFFICE & SUPPLIES
  // ========================================
  "Office Supplies": {
    generalCategory: "Office Supplies",
    federalCategory: "office_expense",
    oregonCategory: "office_expenses",
    confidence: 0.95,
  },
  Software: {
    generalCategory: "Software",
    federalCategory: "office_expense",
    oregonCategory: "office_expenses",
    confidence: 0.9,
  },
  "Computer Hardware": {
    generalCategory: "Computer Hardware",
    federalCategory: "office_expense",
    oregonCategory: "office_expenses",
    confidence: 0.85,
  },
  Electronics: {
    generalCategory: "Electronics",
    federalCategory: "office_expense",
    oregonCategory: "office_expenses",
    confidence: 0.7,
    context: "If for business use",
  },
  Books: {
    generalCategory: "Books",
    federalCategory: "office_expense",
    oregonCategory: "office_expenses",
    confidence: 0.6,
    context: "If professional development",
  },

  // ========================================
  // INSURANCE
  // ========================================
  Insurance: {
    generalCategory: "Insurance",
    federalCategory: "insurance",
    oregonCategory: "insurance",
    confidence: 0.9,
  },
  Medical: {
    generalCategory: "Medical",
    federalCategory: "other_expenses",
    oregonCategory: "other_expenses",
    confidence: 0.7,
    context: "Medical expenses have special rules",
  },
  Dental: {
    generalCategory: "Dental",
    federalCategory: "other_expenses",
    oregonCategory: "other_expenses",
    confidence: 0.7,
  },
  Vision: {
    generalCategory: "Vision",
    federalCategory: "other_expenses",
    oregonCategory: "other_expenses",
    confidence: 0.7,
  },
  "Mental Health": {
    generalCategory: "Mental Health",
    federalCategory: "other_expenses",
    oregonCategory: "other_expenses",
    confidence: 0.7,
  },
  Pharmacy: {
    generalCategory: "Pharmacy",
    federalCategory: "other_expenses",
    oregonCategory: "other_expenses",
    confidence: 0.7,
  },

  // ========================================
  // TRAVEL
  // ========================================
  Airlines: {
    generalCategory: "Airlines",
    federalCategory: "travel",
    oregonCategory: "travel",
    confidence: 0.85,
    context: "If business travel",
  },
  Hotels: {
    generalCategory: "Hotels",
    federalCategory: "travel",
    oregonCategory: "travel",
    confidence: 0.85,
    context: "If business travel",
  },
  Travel: {
    generalCategory: "Travel",
    federalCategory: "travel",
    oregonCategory: "travel",
    confidence: 0.8,
  },
  "Vacation Rentals": {
    generalCategory: "Vacation Rentals",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.9,
    context: "Usually personal expense",
  },
  "Tours and Activities": {
    generalCategory: "Tours and Activities",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.9,
    context: "Usually personal expense",
  },

  // ========================================
  // HOME & MAINTENANCE
  // ========================================
  "Home Improvement": {
    generalCategory: "Home Improvement",
    federalCategory: "repairs_maintenance",
    oregonCategory: "repairs_maintenance",
    confidence: 0.6,
    context: "If for home office",
  },
  "Home Maintenance": {
    generalCategory: "Home Maintenance",
    federalCategory: "repairs_maintenance",
    oregonCategory: "repairs_maintenance",
    confidence: 0.6,
    context: "If for home office",
  },
  Cleaning: {
    generalCategory: "Cleaning",
    federalCategory: "other_expenses",
    oregonCategory: "other_expenses",
    confidence: 0.5,
    context: "If office cleaning",
  },
  "Home and Garden": {
    generalCategory: "Home and Garden",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.8,
    context: "Usually personal expense",
  },
  Furniture: {
    generalCategory: "Furniture",
    federalCategory: "office_expense",
    oregonCategory: "office_expenses",
    confidence: 0.5,
    context: "If office furniture",
  },

  // ========================================
  // ENTERTAINMENT & RECREATION
  // ========================================
  Entertainment: {
    generalCategory: "Entertainment",
    federalCategory: "meals",
    oregonCategory: "meals",
    confidence: 0.3,
    context: "If client entertainment",
  },
  Movies: {
    generalCategory: "Movies",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.95,
    context: "Usually personal expense",
  },
  Music: {
    generalCategory: "Music",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.95,
    context: "Usually personal expense",
  },
  "Streaming Services": {
    generalCategory: "Streaming Services",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.95,
    context: "Usually personal expense",
  },
  Gaming: {
    generalCategory: "Gaming",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.95,
    context: "Usually personal expense",
  },
  "Sports and Outdoors": {
    generalCategory: "Sports and Outdoors",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.95,
  },
  Fitness: {
    generalCategory: "Fitness",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.9,
    context: "Usually personal expense",
  },
  "Sports Events": {
    generalCategory: "Sports Events",
    federalCategory: "meals",
    oregonCategory: "meals",
    confidence: 0.3,
    context: "If client entertainment",
  },

  // ========================================
  // RETAIL & SHOPPING
  // ========================================
  Clothing: {
    generalCategory: "Clothing",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.9,
    context: "Usually personal expense",
  },
  "Department Stores": {
    generalCategory: "Department Stores",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.7,
  },
  "Online Shopping": {
    generalCategory: "Online Shopping",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.5,
    context: "Depends on what was purchased",
  },
  "Specialty Retail": {
    generalCategory: "Specialty Retail",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.6,
  },
  "Pet Supplies": {
    generalCategory: "Pet Supplies",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.95,
    context: "Usually personal expense",
  },
  "Pet Care": {
    generalCategory: "Pet Care",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.95,
  },

  // ========================================
  // PERSONAL CARE
  // ========================================
  "Personal Care": {
    generalCategory: "Personal Care",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.95,
    context: "Usually personal expense",
  },
  Beauty: {
    generalCategory: "Beauty",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.95,
  },
  Spa: {
    generalCategory: "Spa",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.95,
  },

  // ========================================
  // FINANCIAL
  // ========================================
  "Banking Fees": {
    generalCategory: "Banking Fees",
    federalCategory: "other_expenses",
    oregonCategory: "other_expenses",
    confidence: 0.8,
  },
  "ATM Fees": {
    generalCategory: "ATM Fees",
    federalCategory: "other_expenses",
    oregonCategory: "other_expenses",
    confidence: 0.8,
  },
  "Credit Cards": {
    generalCategory: "Credit Cards",
    federalCategory: "interest",
    oregonCategory: "interest",
    confidence: 0.7,
    context: "If business credit card interest",
  },
  Loans: {
    generalCategory: "Loans",
    federalCategory: "interest",
    oregonCategory: "interest",
    confidence: 0.7,
    context: "Business loan interest",
  },
  Investment: {
    generalCategory: "Investment",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.8,
  },
  Taxes: {
    generalCategory: "Taxes",
    federalCategory: "taxes_licenses",
    oregonCategory: "taxes_licenses",
    confidence: 0.9,
  },
  "Court Fees": {
    generalCategory: "Court Fees",
    federalCategory: "legal_professional",
    oregonCategory: "legal_professional_services",
    confidence: 0.8,
  },
  DMV: {
    generalCategory: "DMV",
    federalCategory: "taxes_licenses",
    oregonCategory: "taxes_licenses",
    confidence: 0.8,
    context: "If business vehicle",
  },

  // ========================================
  // EDUCATION & COMMUNITY
  // ========================================
  Education: {
    generalCategory: "Education",
    federalCategory: "other_expenses",
    oregonCategory: "other_expenses",
    confidence: 0.6,
    context: "Professional development may be deductible",
  },
  Charity: {
    generalCategory: "Charity",
    federalCategory: "other_expenses",
    oregonCategory: "other_expenses",
    confidence: 0.8,
    context: "Charitable contributions",
  },
  Religious: {
    generalCategory: "Religious",
    federalCategory: "other_expenses",
    oregonCategory: "other_expenses",
    confidence: 0.8,
    context: "Charitable contributions",
  },
  Community: {
    generalCategory: "Community",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.6,
  },
  Government: {
    generalCategory: "Government",
    federalCategory: "taxes_licenses",
    oregonCategory: "taxes_licenses",
    confidence: 0.7,
  },

  // ========================================
  // OTHER
  // ========================================
  Gifts: {
    generalCategory: "Gifts",
    federalCategory: "other_expenses",
    oregonCategory: "other_expenses",
    confidence: 0.5,
    context: "Business gifts have limits",
  },
  Hobbies: {
    generalCategory: "Hobbies",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.95,
    context: "Usually personal expense",
  },
  Recreation: {
    generalCategory: "Recreation",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.9,
  },
  Transfer: {
    generalCategory: "Transfer",
    federalCategory: "transfer",
    oregonCategory: "transfer",
    confidence: 0.9,
  },
  Income: {
    generalCategory: "Income",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.95,
    context: "Income, not expense",
  },
  Other: {
    generalCategory: "Other",
    federalCategory: "other_expenses",
    oregonCategory: "other_expenses",
    confidence: 0.5,
  },
  Childcare: {
    generalCategory: "Childcare",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.9,
    context: "Special tax credits may apply",
  },
  Family: {
    generalCategory: "Family",
    federalCategory: null,
    oregonCategory: null,
    confidence: 0.95,
    context: "Usually personal expense",
  },
  Advertising: {
    generalCategory: "Advertising",
    federalCategory: "advertising",
    oregonCategory: "advertising",
    confidence: 0.95,
  },
};

/**
 * Get tax category suggestions based on general category
 */
export function getTaxCategorySuggestions(
  generalCategory: string
): CategoryMappingRule | null {
  return GENERAL_TO_TAX_MAPPINGS[generalCategory] || null;
}

/**
 * Get a confidence-based recommendation
 */
export function shouldAutoApplyTaxCategories(
  rule: CategoryMappingRule
): boolean {
  // Auto-apply if confidence is high and no context is needed
  return rule.confidence >= 0.85 && !rule.context;
}

/**
 * Get a human-readable explanation for the mapping
 */
export function getTaxCategoryExplanation(rule: CategoryMappingRule): string {
  if (rule.context) {
    return rule.context;
  }

  if (rule.confidence >= 0.9) {
    return "High confidence based on typical tax categorization";
  } else if (rule.confidence >= 0.7) {
    return "Moderate confidence - review recommended";
  } else {
    return "Low confidence - manual review suggested";
  }
}
